import { FC, ReactNode } from 'react';
import { Popover, SegmentedControl } from '@mantine/core';
import { plural } from '../../lib/pluralize';
import { usePortalTarget } from '../../provider/UiContext';
import { DTB_POPUP_Z } from '../popupZIndex';
import { DataTableWrapMode } from '../DataTable.types';
import { ROW_HEIGHT_HINT, ROW_HEIGHT_LABEL, ROW_HEIGHT_MODES } from '../rowHeight';
import RowHeightIcon from './RowHeightIcon';

interface DataTableSettingsMenuProps {
    opened: boolean;
    onOpenChange: (opened: boolean) => void;
    /** Кнопка-шестерёнка (Popover.Target). */
    target: ReactNode;
    wrapMode: DataTableWrapMode;
    onWrapModeChange: (mode: DataTableWrapMode) => void;
    /** Дополнительные настройки владельца таблицы — рендерятся над высотой строк
     *  (периоды графика поставки и т.п.). */
    extra?: ReactNode;
    /** Сколько строк помещается в списке при текущей высоте — цена выбора
     *  показывается сразу, не закрывая меню. */
    rowsOnScreen?: number;
}

/**
 * Меню «Настройки таблицы» — высота строк плюс настройки владельца.
 *
 * Живёт только там, где кроме высоты строк в меню есть что-то ещё (график
 * поставки): ради одного контрола меню не открывают, для него есть кнопка
 * туллбара DataTableRowHeightButton.
 */
const DataTableSettingsMenu: FC<DataTableSettingsMenuProps> = ({
    opened,
    onOpenChange,
    target,
    wrapMode,
    onWrapModeChange,
    extra,
    rowsOnScreen,
}) => {
    const portalTarget = usePortalTarget();
    return (
        <Popover
            opened={opened}
            onChange={onOpenChange}
            position="bottom-end"
            offset={6}
            // Mantine ставит дропдауну inline width: max-content — ширину задаём пропом,
            // иначе три подписи с иконками жмутся в ellipsis
            width={296}
            zIndex={DTB_POPUP_Z}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
            classNames={{ dropdown: 'dtb-menu dtb-settings' }}
        >
            <Popover.Target>{target}</Popover.Target>
            <Popover.Dropdown>
                {extra}
                {extra ? <hr /> : null}
                <div className="dtb-menu__label">Высота строк</div>
                <SegmentedControl
                    fullWidth
                    className="dtb-rh-seg"
                    data={ROW_HEIGHT_MODES.map((mode) => ({
                        value: mode,
                        label: (
                            <span className="dtb-rh__opt">
                                <RowHeightIcon mode={mode} size={20} />
                                {ROW_HEIGHT_LABEL[mode]}
                            </span>
                        ),
                    }))}
                    value={wrapMode}
                    onChange={onWrapModeChange}
                />
                <div className="dtb-rh__foot">
                    <span>{ROW_HEIGHT_HINT[wrapMode]}</span>
                    {rowsOnScreen ? (
                        <span className="dtb-rh__rows">
                            на экране <b>{rowsOnScreen}</b> {plural(rowsOnScreen, ['строка', 'строки', 'строк'])}
                        </span>
                    ) : null}
                </div>
            </Popover.Dropdown>
        </Popover>
    );
};

export default DataTableSettingsMenu;
